<template>
    <div class="w-full flex banner-container">
        <div
            :class="[
                'max-w-full banner-child flex-grow rounded-banner',
                !disableBorders ? `border border-${modelClass}-accent` : '',
                `bg-${modelClass}-accent text-${modelClass}`,
            ]"
        >
            <Link :href="url" class="relative block">
                <ModelImage
                    :src="
                        modelClass == 'user' || modelClass == 'organization'
                            ? model?.coverImageUrl
                            : model?.imageUrl
                    "
                    :loading="!model"
                    :modelClass="modelClass"
                    imageClasses="w-full h-full"
                    :clickable="true"
                    :class="`image-container aspect-video sm:aspect-3/2 sm:rounded-r-none cursor-pointer bg-${modelClass}`"
                    :typeId="model?.type_id"
                    :fileUrl="model?.file"
                />
                <div
                    v-if="
                        (modelClass == 'user' ||
                            modelClass == 'organization') &&
                        model.imageUrl
                    "
                    :class="[
                        {
                            'rounded-full': modelClass == 'user',
                        },
                        {
                            'rounded-img': modelClass == 'organization',
                        },
                        'absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2',
                    ]"
                >
                    <ModelImage
                        :src="model.imageUrl"
                        :modelClass="modelClass"
                        :clickable="true"
                        :fallbackBorder="`border border-${modelClass}-accent`"
                        :class="[
                            {
                                'rounded-full': modelClass == 'user',
                            },
                            {
                                'rounded-img': modelClass == 'organization',
                            },
                            `border border-${modelClass}-accent`,
                        ]"
                        heightClasses="h-48 aspect-square"
                        classes="flex items-center bg-white"
                        :imageClasses="[{ 'size-48': modelClass == 'user' }]"
                        objectFitClasses="object-center object-cover"
                    />
                </div>
            </Link>
            <div
                class="flex flex-col px-4 py-3 xl:p-8 gap-y-4 flex-shrink-0 content-container"
            >
                <slot name="header" v-if="!$slots.header" />
                <TruncateTooltip v-if="model">
                    <Link
                        v-text="model.name"
                        :href="url"
                        class="content-header pt-0.5 text-3xl xl:text-4xl font-header font-bold underline-hover break-words"
                    />
                </TruncateTooltip>
                <div
                    v-else
                    :class="`w-full rounded-md bg-${modelClass}/50 animate-pulse h-6`"
                />

                <slot name="center" />

                <div class="flex-grow description-container">
                    <p
                        v-if="model"
                        class="text-base"
                        v-text="model.description"
                    />
                    <div v-else class="animate-pulse space-y-2">
                        <div
                            v-for="i in 5"
                            :style="`width: calc(100% - ${i * 7}% + ${
                                Math.floor(Math.random() * 7) + 1
                            }%)`"
                            :class="`rounded-md bg-${modelClass}/50 h-5`"
                        />
                    </div>
                </div>

                <slot name="footer" />
            </div>
        </div>
    </div>
</template>

<script>
import ModelImage from '@/Components/ModelComponents/ModelImage.vue'
import TruncateTooltip from '@/Components/Utilities/TruncateTooltip.vue'
import { Link } from '@inertiajs/vue3'

export default {
    name: 'HighlightTemplate',

    components: {
        ModelImage,
        TruncateTooltip,
        Link,
    },

    props: {
        model: {
            type: [Object, null],
            required: true,
        },
        modelClass: {
            type: String,
            required: true,
        },
        disableBorders: {
            type: Boolean,
            default: true,
        },
    },

    computed: {
        url() {
            if (this.model) {
                return this.$localizedRoute(
                    `${this.modelClass}.show`,
                    this.model.id
                )
            }
            return ''
        },
    },

    mounted() {
        if (!!this.$slots.footer) {
            document.documentElement.style.setProperty(
                '--line-clamp-increase',
                1
            )
        }
    },
}
</script>
<style scoped>
.banner-container {
    container-name: banner-container;
    container-type: inline-size;
}

.banner-container > .banner-child {
    display: flex;
    flex-direction: column;
    @container banner-container (width >= 500px) {
        flex-direction: row;
    }
}

.banner-container > .banner-child > * {
    width: 100%;
    .image-container {
        width: 100%;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
        border-bottom-left-radius: 0px;
        border-bottom-right-radius: 0px;
    }
    @container banner-container (width >= 500px) {
        width: 50%;
        .image-container {
            border-bottom-left-radius: 8px;
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
        }
    }
}

.content-container {
    @container banner-container (width >= 500px) {
        container-name: content-container;
        container-type: size;
        overflow-y: hidden;
    }
}

.content-container .content-header {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 5;
    @container banner-container (width >= 500px) {
        -webkit-line-clamp: 2;

        @container content-container (height >= 280px) {
            -webkit-line-clamp: 3;
        }
        @container content-container (height >= 310px) {
            -webkit-line-clamp: 4;
        }
        @container content-container (height >= 500px) {
            -webkit-line-clamp: 5;
        }
        @container content-container (height >= 550px) {
            -webkit-line-clamp: 6;
        }
    }
}

.description-container {
    @container banner-container (width >= 500px) {
        container-name: description-container;
        container-type: size;
    }
}

.description-container p {
    -webkit-line-clamp: 5;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    @container banner-container (width >= 500px) {
        -webkit-line-clamp: 1;

        @container description-container (height < 25px) {
            display: none;
        }
        @container description-container (height >= 43px) {
            -webkit-line-clamp: calc(1 + var(--line-clamp-increase, 0));
        }
        @container description-container (height >= 50px) {
            -webkit-line-clamp: calc(2 + var(--line-clamp-increase, 0));
        }
        @container description-container (height >= 75px) {
            -webkit-line-clamp: calc(3 + var(--line-clamp-increase, 0));
        }
        @container description-container (height >= 100px) {
            -webkit-line-clamp: calc(4 + var(--line-clamp-increase, 0));
        }
        @container description-container (height >= 120px) {
            -webkit-line-clamp: calc(5 + var(--line-clamp-increase, 0));
        }
        @container description-container (height >= 140px) {
            -webkit-line-clamp: calc(6 + var(--line-clamp-increase, 0));
        }
        @container description-container (height >= 170px) {
            -webkit-line-clamp: calc(7 + var(--line-clamp-increase, 0));
        }
        @container description-container (height >= 200px) {
            -webkit-line-clamp: calc(8 + var(--line-clamp-increase, 0));
        }
        @container description-container (height >= 220px) {
            -webkit-line-clamp: calc(9 + var(--line-clamp-increase, 0));
        }
        @container description-container (height >= 245px) {
            -webkit-line-clamp: calc(10 + var(--line-clamp-increase, 0));
        }
        @container description-container (height >= 270px) {
            -webkit-line-clamp: calc(11 + var(--line-clamp-increase, 0));
        }
        @container description-container (height >= 295px) {
            -webkit-line-clamp: calc(12 + var(--line-clamp-increase, 0));
        }
        @container description-container (height >= 310px) {
            -webkit-line-clamp: calc(13 + var(--line-clamp-increase, 0));
        }
        @container description-container (height >= 340px) {
            -webkit-line-clamp: calc(14 + var(--line-clamp-increase, 0));
        }
        @container description-container (height >= 370px) {
            -webkit-line-clamp: calc(15 + var(--line-clamp-increase, 0));
        }
        @container description-container (height >= 395px) {
            -webkit-line-clamp: calc(16 + var(--line-clamp-increase, 0));
        }
        @container description-container (height >= 415px) {
            -webkit-line-clamp: calc(17 + var(--line-clamp-increase, 0));
        }
        @container description-container (height >= 440px) {
            -webkit-line-clamp: calc(18 + var(--line-clamp-increase, 0));
        }
        @container description-container (height >= 465px) {
            -webkit-line-clamp: calc(19 + var(--line-clamp-increase, 0));
        }
        @container description-container (height >= 490px) {
            -webkit-line-clamp: calc(20 + var(--line-clamp-increase, 0));
        }
    }
}
</style>
